import React, { useState } from 'react'
import { useNavigate, generatePath } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useVenti } from 'venti'
import { SiteConfigurationSummary } from '@matech/thebigpos-sdk'
import { TableColumn } from 'react-data-table-component'

import withAuth from '../../../components/withAuth'
import PartnersExpandedRow from './PartnersExpandedRow'

import { formatDate, getErrorMessage } from '../../../services/helper'
import { eventTypes } from '../../../services/constants'
import { navigationLinking } from '../../../services/navigation'

import { useMixpanel } from '../../../hooks/useMixpanel'
import { useAlert } from '../../../hooks'
import { getTheme } from '../../../config'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import SiteConfigurationPage from '../SiteConfigurationPage'
import UserSiteConfigurationSearchFilters from './UserSiteConfigurationSearchFilters'

const theme = getTheme()

const columns: TableColumn<SiteConfigurationSummary>[] = [
	{
		name: 'First Name',
		selector: (row: SiteConfigurationSummary) =>
			row.realtor?.firstName ?? '',
	},
	{
		name: 'Last Name',
		selector: (row: SiteConfigurationSummary) =>
			row.realtor?.lastName ?? '',
	},
	{
		name: 'Title',
		selector: (row: SiteConfigurationSummary) =>
			row.realtor?.title ?? '',
		hide: theme.breakpoints.dataTable,
	},
	{
		name: 'Email',
		selector: (row: SiteConfigurationSummary) => row.email ?? '',
		sortable: true,
		sortField: 'email',
		hide: theme.breakpoints.dataTable,
	},
	{
		name: 'Brand',
		selector: (row: SiteConfigurationSummary) =>
			row.companyName ?? '',
	},
	{
		name: 'Branch',
		selector: (row: SiteConfigurationSummary) => row.branchName ?? '',
	},
	{
		name: 'URL',
		selector: (row: SiteConfigurationSummary) => row.url ?? '',
		sortable: true,
		sortField: 'url',
		hide: theme.breakpoints.dataTable,
	},
	{
		name: 'Role',
		selector: (row: SiteConfigurationSummary) =>
			row.realtor?.role ?? '',
		hide: theme.breakpoints.dataTable,
	},
	{
		name: 'Created On',
		selector: (row: SiteConfigurationSummary) =>
			row.realtor?.createdAt ?? '',
		cell: (row: SiteConfigurationSummary) =>
			formatDate(row.realtor?.createdAt, false),
		hide: theme.breakpoints.dataTable,
	},
]

const PartnersTable = () => {
	const { alert } = useAlert()
	const ventiState = useVenti()
	const mixpanel = useMixpanel()
	const navigate = useNavigate()

	const [removeModalVisible, setRemoveModalVisible] = useState(false)
	const [activeRow, setActiveRow] =
		useState<SiteConfigurationSummary | null>(null)

	const [filters, setFilters] = useState({
		brand: null,
		type: null,
		branch: null,
	})

	const handleMutationSuccess = (
		eventType: string,
		modalSetter: React.Dispatch<React.SetStateAction<boolean>>,
		successMessage: string,
		activeRow: SiteConfigurationSummary | null
	) => {
		if (!activeRow) return

		const { id, realtor } = activeRow
		const name = `${realtor?.firstName ?? ''} ${realtor?.lastName ?? ''}`
		mixpanel.trackEvent(eventType, {
			id,
			name,
		})
		modalSetter(false)
		alert(`Partner ${name} successfully ${successMessage}`)
	}

	const removePartnerMutation = useMutation({
		mutationFn: (id: string) => TheBigPOSApi.deleteUser(id),
		onSuccess: () => {
			handleMutationSuccess(
				eventTypes.PARTNER_DELETED,
				setRemoveModalVisible,
				'removed',
				activeRow
			)
			setActiveRow(null)
		},
	})

	const removePartner = async () => {
		try {
			if (!activeRow?.realtor?.id) return
			await removePartnerMutation.mutateAsync(activeRow.realtor?.id)
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	const handleFilterChange = (
		value: string | undefined,
		field: string
	) => {
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}))
	}

	const onAddClick = () => {
		navigate(
			generatePath(`/${navigationLinking.AdminPartnerEdit}`, {
				id: 'new',
			})
		)
	}

	const onRowClicked = async (row: SiteConfigurationSummary) => {
		await ventiState.set(
			theme.storageKeys.editingPartnerSiteConfigurationId,
			row.id
		)
		navigate(
			generatePath(`/${navigationLinking.AdminPartnerEdit}`, {
				id: row?.realtor?.id,
			})
		)
	}

	const handleActiveRowChange = (row: SiteConfigurationSummary) => {
		setActiveRow(row)
	}

	return (
		<SiteConfigurationPage
			entityType="Realtor"
			title="Partners"
			onRemove={removePartner}
			isRemovePending={removePartnerMutation.isPending}
			filters={filters}
			activeRow={activeRow}
			onActiveRowChange={handleActiveRowChange}
			columns={columns}
			onRowClicked={onRowClicked}
			removeModalVisible={removeModalVisible}
			onRemoveModalVisible={setRemoveModalVisible}
			searchFilters={
				<UserSiteConfigurationSearchFilters
					filters={filters}
					handleFilterChange={handleFilterChange}
				/>
			}
			expandableRowsComponent={PartnersExpandedRow}
			addNewBtn={{ text: 'Add Partner', onClick: onAddClick }}
		/>
	)
}

export default withAuth(PartnersTable)
